import { UIElement } from '../ui-element';
import {
    sanitizeHTML,
    SANITIZER_DEFAULT_ALLOWED_TAGS_SVG,
    SANITIZER_DEFAULT_ALLOWED_ATTRS_SVG,
} from '../../global/html-sanitizer';
import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import type { UIIconGlyphRedesign } from '../icon/ui-icon.types';
import { UIIcon } from '../icon/ui-icon';
import styles from './ui-sprite.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UISprite
 * @element ui-sprite
 * @classdesc Represents a class for <code>ui-sprite</code> element.
 * Technical element to loads sprite-image or other external resources into page.
 * @property {string} src {@attr src} Source URL of resources.
 * @example
 * <ui-sprite src="/assets/my-sprites.svg"></ui-sprite>
 */
@Component({
    tag: 'ui-sprite',
    styles,
})
export class UISprite extends UIElement {
    private static readonly DefaultMimeType = 'image/xml+svg';
    private hasLoadedGlyphs = false;
    private loadingPromise: Promise<void> | null = null;

    @Attribute(String) src!: string;

    /**
     * @inheritDoc
     */
    observeAttributes(
        name: string,
        _oldValue: string,
        _newValue: string
    ): void {
        if (name === 'src') {
            this.loadSprite();
        }
    }

    /**
     * @deprecated For backward compatibility only.
     */
    getGlyphPrefix(): string {
        return '';
    }

    /**
     * Get all glyphs available in the sprite.
     * @returns {Promise<Array<UIIconGlyphRedesign>>}
     */
    async getAllGlyphs(): Promise<Array<UIIconGlyphRedesign>> {
        if (!this.loadingPromise) {
            this.loadSprite();
        }
        if (this.loadingPromise) {
            await this.loadingPromise;
        }
        const glyphs: Array<UIIconGlyphRedesign> = [];
        this.querySelectorAll<SVGSymbolElement>('symbol').forEach((symbol) => {
            glyphs.push(
                symbol.id.replace(UIIcon.GlyphPrefix, '') as UIIconGlyphRedesign
            );
        });
        return glyphs;
    }

    private loadSprite(): void {
        if (!this.src || this.hasLoadedGlyphs) {
            return;
        }
        this.loadingPromise = this.fetchSprite()
            .then(() => {
                this.hasLoadedGlyphs = true;
            })
            .catch(console.error);
    }

    private async fetchSprite(): Promise<void> {
        const response = await fetch(this.src, {
            method: 'GET',
            headers: { 'Content-Type': UISprite.DefaultMimeType },
        });
        const text = await response.text();
        this.innerHTML = sanitizeHTML(
            text,
            SANITIZER_DEFAULT_ALLOWED_TAGS_SVG,
            SANITIZER_DEFAULT_ALLOWED_ATTRS_SVG
        );
    }

    /**
     * @inheritDoc
     */
    hydrate(): void {
        this.loadSprite();
    }
}
